import { AdvertisingEvent } from '@foundation-player/runtime';
import { CueEnterEvent, CueExitEvent } from '@foundation-player/types';
import { PlayerEvent } from '../../event-emitter/events';
import { CueEvent } from '../../event-emitter/unfiltered';
import { PlayerInterface } from '../../player-interface';

/**
 * This observer takes care of cleaning up subtitles
 * when ads start and end. It will send a `CueExit` event
 * if a subtitle is showing when an ad is started and will
 * restore the subtitle with dispatching a `CueEnter` event.
 */
export class AdvertisingSubtitleObserver {
  private cueEvents: CueEnterEvent[] = [];
  private readonly storedCueEvents: CueEnterEvent[] = [];
  private activeTextTrackId: string | undefined;

  constructor(private readonly player: PlayerInterface) {
    player.addEventListener(PlayerEvent.CueEnter, (event: CueEnterEvent) => this.onCueEnter(event));
    player.addEventListener(PlayerEvent.CueExit, (event: CueExitEvent) => this.onCueExit(event));
    player.addEventListener(AdvertisingEvent.AdSlotStarted, () => this.removeActiveSubtitles());
    player.addEventListener(AdvertisingEvent.AdSlotCompleted, () => this.restoreActiveSubtitles());
  }

  private onCueEnter(event: CueEnterEvent) {
    this.cueEvents.push(event);
  }

  private onCueExit(event: CueExitEvent) {
    this.cueEvents = this.cueEvents.filter((enterEvent: CueEnterEvent) => event.cueId != enterEvent.cueId);
  }

  private removeActiveSubtitles() {
    this.cueEvents.forEach((event: CueEnterEvent) => {
      this.storedCueEvents.push({ ...event });
      this.player.dispatchEvent(
        new CueEvent(PlayerEvent.CueExit, {
          cueId: event.cueId,
        }),
      );
    });

    if (this.player.getTextTrackMode() === 'Vtt') {
      this.activeTextTrackId = this.player.getActiveTextTrack()?.id;
      this.player.disableTextTrack();
    }
  }

  private restoreActiveSubtitles() {
    if (this.player.getTextTrackMode() === 'Vtt' && this.activeTextTrackId) {
      this.player.enableTextTrack(this.activeTextTrackId);
    }

    this.storedCueEvents.forEach((event: CueEnterEvent) => {
      this.player.dispatchEvent(new CueEvent(PlayerEvent.CueEnter, { ...event }));
    });
  }
}
